import { publishElementsDataType } from '../../Pages/Flo/Flo.types';
import React, {
	CSSProperties,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { get, max, min } from 'lodash';
import classNames from 'classnames';
import {
	IconArrowRight,
	IconChevronUp,
	IconPlayerPauseFilled,
	IconPlayerPlayFilled,
} from '@tabler/icons-react';
import Processing from '../../Pages/Flo/Components/Processing/Processing';
import { calculateAspectRatioFit } from '../../utils/video.utils';
import { scaleLinear } from 'd3-scale';
import { Button, Tooltip } from '@mui/material';
import { updateDialogAction } from '../VideoDialog/VideoDialog.reducer';
import { useDispatch } from 'react-redux';
import { IMAGE_NEXT_BUTTON_TOOLTIP } from '../../Common/Common.constants';
import { setAutoPlay } from '../../Pages/Flo/Flo.actions';
import styles from './ImageElement.module.css';

const errorImage = 'https://zeus.floik.com/system/w3/media/images/not-found-avatar.svg';
const parser = new DOMParser();

export const ImageElement = ({
	currentElement,
	currentElementIndex,
	goToStep,
	playerDim,
	hasNotPlayed,
	setCountDownPause,
	isWidthLessThan650,
	isDocument,
	elementVoiceResources,
	togglePause,
	parentLoading,
	floId,
	floIndex,
	recordingElement,
}: {
	setCountDownPause?(value: boolean): void;
	togglePause?(value: boolean): void;
	currentElementIndex?: number;
	currentElement?: publishElementsDataType | null;
	goToStep?(currentElementId?: string, next?: boolean, arrowClick?: boolean): void;
	playerDim: {
		width: number;
		height: number;
	};
	hasNotPlayed?: boolean;
	isWidthLessThan650?: boolean;
	isDocument?: boolean;
	elementVoiceResources?:
		| { [key: string]: { voiceId: string; scriptId: string } }
		| undefined;
	parentLoading?: boolean;
	floId: string;
	floIndex: number;
	recordingElement?: publishElementsDataType | null;
}) => {
	const dispatch = useDispatch();
	const [imageDimension, setImageDimension] = useState<CSSProperties>({});
	const [naturalImageSize, setNaturalImageSize] = useState<{
		width: number;
		height: number;
	}>({
		width: 0,
		height: 0,
	});
	const imageRef = React.useRef<HTMLImageElement | null>(null);
	const [imageReady, setImageReady] = useState('loading');
	const [ctaUpButtonClicked, setCtaUpButtonClicked] = useState(false);

	const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
	const [countDown, setCountDown] = useState(-1);
	const [pauseCountDown, setPauseCountDown] = useState(true);

	const voiceProperties = get(
		elementVoiceResources,
		`${get(currentElement, 'voiceConfig.scriptId')}_${get(
			currentElement,
			'voiceConfig.voiceId'
		)}`
	);

	const duration = useMemo(() => {
		const voiceDuration =
			get(recordingElement, 'duration', 0) ??
			Math.ceil(get(voiceProperties, 'durationInMillis', 0) / 1000);
		return max([voiceDuration, get(currentElement, 'duration', 1)]);
	}, [voiceProperties, currentElement, recordingElement]);

	const loadNextStep = useCallback(() => {
		// goToStep(undefined, true);
		setCountDown((i) => i - 1);
		if (get(currentElement, 'editorElementName') !== 'image') {
			if (timer) {
				clearInterval(timer);
				setTimer(null);
				setCountDown(0);
				setImageReady('loading');
			}
		}
	}, [currentElement, timer, setTimer, setCountDown, setImageReady]);

	const startCountdown = useCallback(() => {
		if (
			get(currentElement, 'behaviourType') &&
			get(currentElement, 'behaviourType') !== 'stayFor'
		)
			return;
		setCountDown(duration);
		const newTimer = setInterval(loadNextStep, 1000);
		setTimer(newTimer);
		setPauseCountDown(false);
	}, [setPauseCountDown, setTimer, currentElement, setCountDown, loadNextStep, duration]);

	const onImageErrored = useCallback(() => {
		if (timer) {
			clearInterval(timer);
		}
		setImageReady('errored');
		startCountdown();
	}, [startCountdown, setImageReady, timer]);

	const onImageLoaded = useCallback(() => {
		if (timer) {
			clearInterval(timer);
		}
		setImageReady('loaded');
		if (!hasNotPlayed) {
			startCountdown();
		}
	}, [hasNotPlayed, startCountdown, timer, setImageReady]);

	const onTogglePauseCountDown = useCallback(
		(e: React.MouseEvent) => {
			dispatch(setAutoPlay({ data: false, floId, floIndex }));
			e.stopPropagation();
			if (
				imageReady === 'loading' ||
				(get(currentElement, 'behaviourType') &&
					get(currentElement, 'behaviourType') !== 'stayFor')
			)
				return;
			setPauseCountDown((i) => !i);
			if (timer) {
				clearInterval(timer);
				setTimer(null);
				togglePause?.(true);
			} else {
				const newTimer = setInterval(loadNextStep, 1000);
				setTimer(newTimer);
				togglePause?.(false);
			}
		},
		[
			onImageLoaded,
			setTimer,
			setPauseCountDown,
			timer,
			imageReady,
			currentElement,
			togglePause,
			floId,
		]
	);

	useEffect(() => {
		if (countDown === -1 && timer) {
			clearInterval(timer);
			setCountDown(0);
			setTimer(null);
			goToStep?.(undefined, true);
		}
	}, [setTimer, setCountDown, goToStep, countDown, timer]);

	useEffect(() => {
		if (get(currentElement, 'editorElementName') === 'image') {
			const playerWidth = (get(playerDim, 'width', 0) as number) || 16;
			const playerHeight = (get(playerDim, 'height', 0) as number) || 9;
			const canvasWindowWidth = get(
				currentElement,
				'canvasRenderConfig.windowCoordinates.width',
				0
			);
			const canvasWindowHeight = get(
				currentElement,
				'canvasRenderConfig.windowCoordinates.height',
				0
			);
			const canvasCoordinates = get(
				currentElement,
				'canvasRenderConfig.canvasCoordinates'
			);

			const xScale = scaleLinear([0, canvasWindowWidth], [0, playerWidth]);
			const yScale = scaleLinear([0, canvasWindowHeight], [0, playerHeight]);
			const imageMaxWidth = canvasWindowWidth || get(naturalImageSize, 'width', 0);
			const imageMaxHeight = canvasWindowHeight || get(naturalImageSize, 'height', 0);
			const fitToScreen = isDocument ? get(currentElement, 'fitToScreen', false) : false;
			const maxWidth = fitToScreen ? undefined : imageMaxWidth;
			const maxHeight = fitToScreen ? undefined : imageMaxHeight;

			let finalImageSize: {
				width: number;
				height: number;
				left: number;
				top: number;
				transform?: string;
				maxWidth?: string | number;
				maxHeight?: string | number;
			} = {
				width: 0,
				height: 0,
				left: 0,
				top: 0,
				transform: '',
			};
			if (fitToScreen) {
				const imageSize = calculateAspectRatioFit(
					imageMaxWidth,
					imageMaxHeight,
					playerWidth,
					playerHeight
				);
				const newWidth = (min([
					imageSize.width,
					fitToScreen ? undefined : imageMaxWidth,
				]) || 0) as number;
				const newHeight = (min([
					imageSize.height,
					fitToScreen ? undefined : imageMaxHeight,
				]) || 0) as number;
				finalImageSize = {
					width: newWidth,
					height: newHeight,
					left: (get(playerDim, 'width', 0) - newWidth) / 2,
					top: (get(playerDim, 'height', 0) - newHeight) / 2,
				};
			} else {
				finalImageSize = {
					width: xScale(get(canvasCoordinates, 'width', 0)),
					height: yScale(get(canvasCoordinates, 'height', 0)),
					left: xScale(get(canvasCoordinates, 'x', 0)),
					top: yScale(get(canvasCoordinates, 'y', 0)),
					transform: get(currentElement, 'fitToScreen', false)
						? ''
						: `rotate(${get(canvasCoordinates, 'angle', 0)}deg)`,
				};
				if (!get(currentElement, 'behaviourType', false) && !isDocument) {
					finalImageSize = {
						...finalImageSize,
						maxWidth,
						maxHeight,
						// @ts-ignore
						objectFit: 'contain',
					};
				}
			}
			setImageDimension(finalImageSize);
		}
	}, [
		isDocument,
		naturalImageSize,
		playerDim.width,
		playerDim.height,
		setImageDimension,
		currentElement,
	]);

	useEffect(() => {
		return () => {
			if (timer) clearInterval(timer);
			setCountDownPause?.(true);
		};
	}, [timer, setCountDownPause]);

	useEffect(() => {
		setCountDownPause?.(!timer);
	}, [timer, setCountDownPause]);

	useEffect(() => {
		setNaturalImageSize({
			width: get(imageRef, 'current.naturalWidth', 0),
			height: get(imageRef, 'current.naturalHeight', 0),
		});
	}, [setNaturalImageSize, imageReady, imageRef.current]);

	const onNextArrowClick = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			goToStep?.(undefined, true);
		},
		[goToStep]
	);

	const showCtaClick = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			dispatch(
				updateDialogAction({
					data: {
						visible: true,
					},
					floId,
					floIndex,
				})
			);
			setCtaUpButtonClicked(true);
		},
		[setCtaUpButtonClicked, floId]
	);

	const isSvg = get(currentElement, 'imageUrl', '').includes('.svg');

	useEffect(() => {
		if (isSvg) {
			fetch(get(currentElement, 'imageUrl', ''))
				.then((response) => response.text())
				.then((text) => {
					const parsed = parser.parseFromString(text, 'text/html');
					const svg = parsed.querySelector('svg');
					svg?.setAttribute('style', `fill:var(--global-themeColor);`);
					const svgWrapper = document.getElementById('FLOIK_SVG_IMAGE_WRAPPER');
					if (svgWrapper) {
						while (svgWrapper.hasChildNodes()) {
							//@ts-ignore
							svgWrapper.removeChild(svgWrapper.firstChild);
						}
						//@ts-ignore
						svgWrapper.appendChild(svg);
					}
				});
		}
	}, [get(currentElement, 'imageUrl')]);

	return (
		<div
			className={classNames(styles.imageContainer, {
				[styles.disable]: imageReady === 'loading',
				[styles.hasNotPlayed]: hasNotPlayed && !get(currentElement, 'linkedCtaId'),
			})}
			onClick={onTogglePauseCountDown}
		>
			{isSvg && (
				<div
					key={`FLOIK_SVG_IMAGE_WRAPPER_${get(currentElement, 'id')}`}
					id="FLOIK_SVG_IMAGE_WRAPPER"
					className={styles.image}
					style={imageDimension}
				></div>
			)}
			<img
				ref={imageRef}
				onLoad={imageReady === 'errored' ? onImageErrored : onImageLoaded}
				onError={onImageErrored}
				className={classNames(styles.image, { [styles.hideImage]: isSvg })}
				style={imageDimension}
				key={get(currentElement, 'id')}
				src={imageReady === 'errored' ? errorImage : get(currentElement, 'imageUrl')}
				alt=""
			/>
			{!parentLoading && imageReady === 'loading' && <Processing text="loading" />}
			{!isDocument && (
				<>
					{get(currentElement, 'behaviourType') === 'nextButton' && (
						<Tooltip
							title={IMAGE_NEXT_BUTTON_TOOLTIP}
							arrow
							placement="top"
							classes={{
								popper: styles.tooltip,
							}}
						>
							<Button className={styles.nextButton} onClick={onNextArrowClick}>
								<div className={styles.arrowBar}></div>
								<IconArrowRight className={styles.nextIcon} size={18} />
							</Button>
						</Tooltip>
					)}
					{isWidthLessThan650 &&
						get(currentElement, 'behaviourType') === 'CTA' &&
						!ctaUpButtonClicked && (
							<Button className={styles.showCtaButton} onClick={showCtaClick}>
								<IconChevronUp className={styles.upIcon} size={20}></IconChevronUp>
							</Button>
						)}
					{(!get(currentElement, 'behaviourType') ||
						get(currentElement, 'behaviourType') === 'stayFor') &&
						(countDown > 0 || pauseCountDown) &&
						!hasNotPlayed && (
							<div
								className={classNames(styles.imageCountDown, {
									[styles.pauseAnimation]: pauseCountDown,
								})}
								style={{
									// @ts-ignore
									'--anim-completed': countDown - 1,
									'--anim-duration': duration,
								}}
							>
								{pauseCountDown ? '' : countDown}

								<div className={styles.timer}></div>
								<div
									className={styles.toggleButtonContainer}
									data-testid={pauseCountDown ? 'play-icon' : 'pause-icon'}
								>
									{pauseCountDown ? (
										<IconPlayerPlayFilled size={16}></IconPlayerPlayFilled>
									) : (
										<IconPlayerPauseFilled size={16}></IconPlayerPauseFilled>
									)}
								</div>
							</div>
						)}
				</>
			)}
		</div>
	);
};
